import { VALUES } from '@monofront/business/constants';
import BadgeMadeInFR from '@monofront/design-system/visuals/Icons/BadgeMadeInFR';
import BadgeOriginFR from '@monofront/design-system/visuals/Icons/BadgeOriginFR';
import BadgeZeroWaste from '@monofront/design-system/visuals/Icons/BadgeZeroWaste';
import BadgePrivateLabel from '@monofront/design-system/visuals/Icons/BadgePrivateLabel';
import BadgeVegan from '@monofront/design-system/visuals/Icons/BadgeVegan';
import BadgeDemeter from '@monofront/design-system/visuals/Icons/BadgeDemeter';
import BadgeFairTrade from '@monofront/design-system/visuals/Icons/BadgeFairTrade';
import BadgeReturnableContainer from '@monofront/design-system/visuals/Icons/BadgeReturnableContainer';
import BadgeAntiWaste from '@monofront/design-system/visuals/Icons/BadgeAntiWaste';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import BadgeFrozenPrice from '@monofront/design-system/visuals/Icons/BadgeFrozenPrice';
import BadgeFallingPrice from '@monofront/design-system/visuals/Icons/BadgeFallingPrice';
import ListItem, { IValueData } from './ListItem';

interface IValuesProps {
  values: string[];
  size?: 'md' | 'lg';
  enableSidePanel?: boolean;
}

const Values: React.FC<IValuesProps> = ({
  values,
  size = 'md',
  enableSidePanel = false,
}) => {
  const tCatalog = useTranslations('Catalog');
  const filteredValues = useMemo(
    () =>
      values
        .map((value) => value.toUpperCase())
        .filter(
          (value, _, arr) =>
            !(
              value === VALUES.MADEINFRANCE && arr.includes(VALUES.ORIGINFRANCE)
            ),
        ),
    [values],
  );

  const valuesData = useMemo(
    (): { [x: string]: IValueData } => ({
      [VALUES.MADEINFRANCE]: {
        Badge: (props) => <BadgeMadeInFR {...props} />,
        label: tCatalog('tagMadeInFrance'),
        documentUid: 'made-in-france',
      },
      [VALUES.ORIGINFRANCE]: {
        Badge: (props) => <BadgeOriginFR {...props} />,
        label: tCatalog('tagOriginFrance'),
        documentUid: 'origin-france',
      },
      [VALUES.PRIVATELABEL]: {
        Badge: (props) => <BadgePrivateLabel {...props} />,
        label: tCatalog('tagPrivateLabel'),
        documentUid: 'private-label',
      },
      [VALUES.ZEROWASTE]: {
        Badge: (props) => <BadgeZeroWaste {...props} />,
        label: tCatalog('tagZeroWaste'),
        documentUid: 'zero-waste',
      },
      [VALUES.VEGAN]: {
        Badge: (props) => <BadgeVegan {...props} />,
        label: tCatalog('tagVegan'),
        documentUid: 'vegan',
      },
      [VALUES.ANTIWASTE]: {
        Badge: (props) => <BadgeAntiWaste {...props} />,
        label: tCatalog('tagAntiwaste'),
        documentUid: 'anti-waste',
      },
      [VALUES.DEMETER]: {
        Badge: (props) => <BadgeDemeter {...props} />,
        label: tCatalog('tagDemeter'),
      },
      [VALUES.FAIRTRADE]: {
        Badge: (props) => <BadgeFairTrade {...props} />,
        label: tCatalog('tagFairTrade'),
        documentUid: 'fair-trade',
      },
      [VALUES.RETURNABLEPACKAGING]: {
        Badge: (props) => <BadgeReturnableContainer {...props} />,
        label: tCatalog('tagReturnablePackaging'),
        documentUid: 'returnable-packaging',
      },
      [VALUES.FROZENPRICE]: {
        Badge: (props) => <BadgeFrozenPrice {...props} />,
        label: tCatalog('tagFrozenPrice'),
      },
      [VALUES.FALLINGPRICE]: {
        Badge: (props) => <BadgeFallingPrice {...props} />,
        label: tCatalog('tagFallingPrice'),
      },
    }),
    [tCatalog],
  );

  return (
    <>
      {filteredValues.map((value, index) => {
        const data = valuesData[value];

        if (!data) return null;

        return (
          <ListItem
            key={index}
            valueId={value}
            valueData={data}
            size={size}
            enableSidePanel={enableSidePanel}
          />
        );
      })}
    </>
  );
};

export default Values;
